import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Results from './Results';
import { MyTimer } from 'components/Timer/Timer';
import { FiClock, FiCheck, FiAlertCircle, FiLoader } from 'react-icons/fi';

interface OnlineTest {
  _id: string;
  clientId: any;
  businessId: any;
  questions: {
    _id: string;
    title: string;
    options: string[];
    questionType?: 'text' | 'image';
    imageUrl?: string;
    imageDisplayDuration?: number;
  }[];
  coverImage: string;
  expiry: string;
  isSubmitted: boolean;
  timeLimit: number;
  startedAt: Date;
  isReady: boolean;
  createdAt: string;
  updatedAt: string;
  // New fields for image-based tests
  isImageBased?: boolean;
  testImageUrl?: string;
  imageDisplayDuration?: number;
  // New field for results visibility
  showResultsToClient?: boolean;
}

interface Answer {
  question: string;
  answer: string;
}

const OnlineTest: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [onlineTest, setOnlineTest] = useState<OnlineTest | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState<Answer[]>([]);
  
  // States for image-based questions
  const [currentImageQuestion, setCurrentImageQuestion] = useState<number | null>(null);
  const [imageVisible, setImageVisible] = useState<boolean>(false);
  const [imageTimer, setImageTimer] = useState<number>(0);
  const [imageQuestionAnswered, setImageQuestionAnswered] = useState<Set<string>>(new Set());
  
  // States for test image display
  const [testImageVisible, setTestImageVisible] = useState<boolean>(false);
  const [testImageTimer, setTestImageTimer] = useState<number>(0);
  const [testImageExpired, setTestImageExpired] = useState<boolean>(false);

  useEffect(() => {
    handleGetOnlineTest();
  }, []);

  // Timer effect for image display
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (imageVisible && imageTimer > 0) {
      interval = setInterval(() => {
        setImageTimer(prev => {
          if (prev <= 1) {
            setImageVisible(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [imageVisible, imageTimer]);

  // Timer effect for test image display
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (testImageVisible && testImageTimer > 0) {
      interval = setInterval(() => {
        setTestImageTimer(prev => {
          if (prev <= 1) {
            setTestImageVisible(false);
            setTestImageExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [testImageVisible, testImageTimer]);

  // Check if test image should be shown or has expired
  useEffect(() => {
    if (onlineTest?.isImageBased && onlineTest?.isReady && !testImageExpired) {
      const startedAt = new Date(onlineTest.startedAt).getTime();
      const currentTime = new Date().getTime();
      const timeDifference = (currentTime - startedAt) / 1000; // in seconds
      const imageDisplayDuration = onlineTest.imageDisplayDuration || 30;
      
      if (timeDifference < imageDisplayDuration) {
        // Image should still be visible
        const remainingTime = imageDisplayDuration - timeDifference;
        setTestImageTimer(Math.ceil(remainingTime));
        setTestImageVisible(true);
      } else {
        // Image has expired
        setTestImageExpired(true);
      }
    }
  }, [onlineTest, testImageExpired]);

  const handleGetOnlineTest = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URI}/OnlineTest/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setOnlineTest(res.data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load the test. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateTest = () => {
    return answers.length === onlineTest?.questions.length;
  };

  const handleSubmit = async () => {
    if (!validateTest()) {
      toast.error('Please answer all questions before submitting.');
      return;
    }
    try {
      setSubmitting(true);
      await axios.put(
        `${process.env.REACT_APP_BASE_URI}/OnlineTest/${id}/submit`,
        { answers },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      toast.success('Test submitted successfully');
      window.location.reload();
    } catch (error: any) {
      if (error.response?.data?.message === 'Time limit exceeded.') {
        toast.error(
          'Time limit exceeded. Your test has been automatically submitted.'
        );
      } else {
        toast.error('An error occurred while submitting. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartTest = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URI}/OnlineTest/${id}/start`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      handleGetOnlineTest();
    } catch (error) {
      toast.error('Failed to start the test. Please try again.');
    }
  };

  const isTheTestExpired = () => {
    if (!onlineTest?.startedAt || !onlineTest?.timeLimit) return false;
    const startedAt = new Date(onlineTest.startedAt).getTime();
    const currentTime = new Date().getTime();
    const timeDifference = currentTime - startedAt;
    const timeDifferenceInMinutes = timeDifference / 60000;
    return timeDifferenceInMinutes > onlineTest.timeLimit;
  };

  // Functions for image-based questions
  const startImageQuestion = (questionIndex: number, questionId: string) => {
    const question = onlineTest?.questions.find(q => q._id === questionId);
    if (question?.questionType === 'image' && question.imageDisplayDuration) {
      setCurrentImageQuestion(questionIndex);
      setImageVisible(true);
      setImageTimer(question.imageDisplayDuration);
    }
  };

  const handleImageQuestionAnswer = (questionId: string, answer: string) => {
    const newAnswers = answers.filter(
      (ans) => ans.question !== questionId
    );
    setAnswers([
      ...newAnswers,
      {
        question: questionId,
        answer: answer,
      },
    ]);
    setImageQuestionAnswered(prev => new Set(Array.from(prev).concat(questionId)));
  };

  const handleRegularQuestionAnswer = (questionId: string, answer: string) => {
    const newAnswers = answers.filter(
      (ans) => ans.question !== questionId
    );
    setAnswers([
      ...newAnswers,
      {
        question: questionId,
        answer: answer,
      },
    ]);
  };

  const getImageQuestions = () => {
    const imageQuestions = onlineTest?.questions.filter(q => q.questionType === 'image') || [];
    console.log('Image questions found:', imageQuestions);
    return imageQuestions;
  };

  const getTextQuestions = () => {
    const textQuestions = onlineTest?.questions.filter(q => !q.questionType || q.questionType === 'text') || [];
    console.log('Text questions found:', textQuestions);
    console.log('All questions:', onlineTest?.questions);
    return textQuestions;
  };

  // Render image-based question component
  const renderImageQuestion = (question: any, index: number, themeColors: any) => {
    const isCurrentlyViewing = currentImageQuestion === index;
    const isAnswered = imageQuestionAnswered.has(question._id);

    return (
      <div key={question._id} className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 mb-6">
        <div className="text-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Image Question {index + 1}
          </h3>
          {!isCurrentlyViewing && !isAnswered && (
            <button
              onClick={() => startImageQuestion(index, question._id)}
              className={`px-6 py-3 ${themeColors.button} text-white font-bold rounded-full transition duration-300 ease-in-out transform hover:scale-105`}
            >
              View Image & Start Question
            </button>
          )}
        </div>

        {isCurrentlyViewing && (
          <div className="space-y-4">
            {imageVisible && (
              <div className="text-center">
                <div className="mb-4">
                  <div className={`inline-block px-4 py-2 ${themeColors.timer} text-white rounded-full font-bold`}>
                    Image disappears in: {imageTimer}s
                  </div>
                </div>
                <img
                  src={question.imageUrl}
                  alt="Question Image"
                  className="max-w-full h-auto rounded-lg shadow-lg mx-auto max-h-96 object-contain"
                />
              </div>
            )}
            
            <div className="mt-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">
                {question.title}
              </h4>
              <div className="space-y-3">
                {question.options.map((option: string, i: number) => (
                  <label
                    key={i}
                    className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition duration-200"
                  >
                    <input
                      type="radio"
                      name={question._id}
                      value={option}
                      onChange={(e) => handleImageQuestionAnswer(question._id, e.target.value)}
                      className={`form-radio h-5 w-5 ${themeColors.radio}`}
                    />
                    <span className="text-gray-700 font-medium">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {isAnswered && !isCurrentlyViewing && (
          <div className="text-center text-green-600 font-semibold">
            ✓ Question Completed
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <FiLoader className="animate-spin h-12 w-12 text-indigo-600" />
      </div>
    );
  }

  if (!onlineTest) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <FiAlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h2 className="mt-2 text-lg font-semibold text-gray-900">
            Online test not found
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            The test you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  if (onlineTest.isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="text-center mb-8">
          <FiCheck className="mx-auto h-16 w-16 text-green-500" />
          <h2 className="mt-2 text-3xl font-bold text-gray-900">
            Test Submitted
          </h2>
          <p className="mt-2 text-lg text-gray-600">
            Your test has been successfully submitted.
          </p>
          {onlineTest.showResultsToClient === false && (
            <p className="mt-4 text-sm text-gray-500 bg-blue-50 border border-blue-200 rounded-lg p-3">
              Results will be reviewed and shared with you by the administrator.
            </p>
          )}
        </div>
        {/* Show results only if showResultsToClient is true (default) or undefined (backward compatibility) */}
        {(onlineTest.showResultsToClient !== false) && (
          <>
            {/* @ts-ignore */}
            <Results onlineTest={onlineTest} />
          </>
        )}
      </div>
    );
  }

  if (isTheTestExpired()) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <FiAlertCircle className="mx-auto h-16 w-16 text-red-500" />
          <h2 className="mt-2 text-3xl font-bold text-gray-900">
            Test Expired
          </h2>
          <p className="mt-2 text-lg text-gray-600">
            The time limit for this test has been exceeded.
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Please contact the administrator for assistance.
          </p>
        </div>
      </div>
    );
  }

  // Theme 1: Teal Theme (businessId: '66a9ed8034d42fcedbc7aaf2')
  if (onlineTest?.businessId === '66a9ed8034d42fcedbc7aaf2') {
    return (
      <div className="min-h-screen bg-[#38afa9] py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="relative h-48 bg-[#38afa9]">
            <img
              src={onlineTest.coverImage}
              alt="Test cover"
              className="w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <h1 className="text-4xl font-extrabold text-white tracking-tight">
                Preliminary Evaluation Test
              </h1>
            </div>
          </div>

          <div className="p-8">
            <div className="flex justify-between items-start mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {onlineTest.clientId.name}
                </h2>
                <p className="text-[#38afa9]">{onlineTest.clientId.email}</p>
              </div>
              <div className="text-right">
                <h3 className="text-lg font-semibold text-gray-700">
                  Time Limit
                </h3>
                <p className="text-[#38afa9] flex items-center justify-end mt-1 text-xl">
                  <FiClock className="mr-2" />
                  {onlineTest.timeLimit} minutes
                </p>
              </div>
            </div>

            {!onlineTest.isReady ? (
              <div className="text-center py-12">
                <p className="text-xl text-gray-600 mb-8">
                  Shall we start the test?
                </p>
                <button
                  onClick={handleStartTest}
                  className="px-8 py-3 bg-[#38afa9] text-white font-bold rounded-full transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50"
                >
                  Start
                </button>
              </div>
            ) : (
              <>
                <div className="mb-8 bg-gray-100 rounded-lg p-4 sticky top-0 z-10">
                  <MyTimer
                    expiryTimestamp={
                      new Date(
                        new Date(onlineTest.startedAt).getTime() +
                          onlineTest.timeLimit * 60000
                      )
                    }
                  />
                </div>

                {/* Monitoring Notice */}
                <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                  <div className="flex items-center">
                    <FiAlertCircle className="text-red-500 mr-3 h-5 w-5" />
                    <p className="text-red-800 font-medium">
                      You are monitored during this test
                    </p>
                  </div>
                </div>

                {/* Test Image Display for Image-based Tests */}
                {onlineTest.isImageBased && testImageVisible && !testImageExpired && (
                  <div className="mb-12 text-center">
                    <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-[#38afa9]">
                      <div className="mb-6">
                        <div className="inline-block px-6 py-3 bg-red-500 text-white rounded-full font-bold text-lg">
                          Image disappears in: {testImageTimer}s
                        </div>
                      </div>
                      <div className="mb-6">
                        <img
                          src={onlineTest.testImageUrl}
                          alt="Test Image"
                          className="max-w-full h-auto rounded-lg shadow-lg mx-auto max-h-96 object-contain"
                        />
                      </div>
                      <p className="text-gray-600 text-lg">
                        Study this image carefully. Questions will appear after the timer ends.
                      </p>
                    </div>
                  </div>
                )}

                {/* Questions Section - Only show if not image-based OR image has expired */}
                {(!onlineTest.isImageBased || (onlineTest.isImageBased && testImageExpired)) && (
                  <>
                    {/* Image-based Questions Section */}
                    {getImageQuestions().length > 0 && (
                      <div className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                          Image-Based Questions
                        </h2>
                        <div className="space-y-6">
                          {getImageQuestions().map((question, index) => 
                            renderImageQuestion(question, index, {
                              button: 'bg-[#38afa9] hover:bg-[#2e9089]',
                              timer: 'bg-red-500',
                              radio: 'text-[#38afa9]'
                            })
                          )}
                        </div>
                      </div>
                    )}

                    {/* Regular Text Questions Section */}
                    {getTextQuestions().length > 0 && (
                      <div className="space-y-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                          Text Questions
                        </h2>
                        {getTextQuestions().map((question, index) => (
                          <div
                            key={question._id}
                            className="bg-gray-50 rounded-xl p-6 shadow-md"
                          >
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">
                              Question {getImageQuestions().length + index + 1}: {question.title}
                            </h3>
                            <div className="space-y-3">
                              {question.options.map((option, i) => (
                                <label
                                  key={i}
                                  className="flex items-center space-x-3 cursor-pointer hover:bg-gray-100 p-2 rounded-md transition duration-200"
                                >
                                  <input
                                    type="radio"
                                    name={question._id}
                                    value={option}
                                    onChange={(e) => handleRegularQuestionAnswer(question._id, e.target.value)}
                                    className="form-radio h-5 w-5 text-[#38afa9]"
                                  />
                                  <span className="text-gray-700">{option}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-12 flex justify-end">
                      <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="inline-flex items-center px-8 py-3 border border-transparent text-lg font-medium rounded-full shadow-sm text-white bg-[#38afa9] disabled:opacity-50 transition duration-300 ease-in-out transform hover:scale-105"
                      >
                        {submitting ? (
                          <>
                            <FiLoader className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                            Submitting...
                          </>
                        ) : (
                          'Submit Assessment'
                        )}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Theme 2: Yellow Theme (businessId: '66cde879656128f03c987f8e')
  if (onlineTest?.businessId === '66cde879656128f03c987f8e') {
    return (
      <div className="min-h-screen bg-yellow-200 py-10 px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto bg-yellow-50 rounded-lg shadow-lg overflow-hidden">
          <div className="relative h-48 sm:h-60 bg-yellow-400">
            <img
              src={onlineTest.coverImage}
              alt="Test cover"
              className="w-full h-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 sm:px-0">
              <h1 className="text-3xl sm:text-4xl font-bold text-white drop-shadow-md tracking-wide text-center">
                Assessment Test
              </h1>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
              <div className="mb-4 sm:mb-0">
                <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800">
                  {onlineTest.clientId.name}
                </h2>
                <p className="text-sm sm:text-md text-gray-500">
                  {onlineTest.clientId.email}
                </p>
              </div>
              <div className="text-left sm:text-right sm:flex-shrink-0">
                <h3 className="text-md sm:text-lg font-semibold text-gray-600">
                  Time Limit
                </h3>
                <p className="text-yellow-600 flex flex-col sm:flex-row items-start sm:items-center justify-end mt-1 text-md sm:text-xl font-semibold">
                  <FiClock className="mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="leading-tight sm:leading-normal">
                    {onlineTest.timeLimit} minutes
                  </span>
                </p>
              </div>
            </div>

            {!onlineTest.isReady ? (
              <div className="text-center py-12">
                <p className="text-md sm:text-lg text-gray-600 mb-4">
                  Ready to begin the test?
                </p>
                <button
                  onClick={handleStartTest}
                  className="px-6 sm:px-8 py-2 sm:py-3 bg-yellow-500 text-white font-semibold rounded-full shadow transition-transform duration-300 ease-in-out transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  Start Test
                </button>
              </div>
            ) : (
              <>
                <div className="mb-8 bg-white rounded-lg p-4 sm:p-6 shadow sticky top-0 z-20">
                  <MyTimer
                    expiryTimestamp={
                      new Date(
                        new Date(onlineTest.startedAt).getTime() +
                          onlineTest.timeLimit * 60000
                      )
                    }
                  />
                </div>

                {/* Monitoring Notice */}
                <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                  <div className="flex items-center">
                    <FiAlertCircle className="text-red-500 mr-3 h-5 w-5" />
                    <p className="text-red-800 font-medium">
                      You are monitored during this test
                    </p>
                  </div>
                </div>

                   {/* Test Image Display for Image-based Tests */}
                {onlineTest.isImageBased && testImageVisible && !testImageExpired && (
                  <div className="mb-12 text-center">
                    <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-[#38afa9]">
                      <div className="mb-6">
                        <div className="inline-block px-6 py-3 bg-red-500 text-white rounded-full font-bold text-lg">
                          Image disappears in: {testImageTimer}s
                        </div>
                      </div>
                      <div className="mb-6">
                        <img
                          src={onlineTest.testImageUrl}
                          alt="Test Image"
                          className="max-w-full h-auto rounded-lg shadow-lg mx-auto max-h-96 object-contain"
                        />
                      </div>
                      <p className="text-gray-600 text-lg">
                        Study this image carefully. Questions will appear after the timer ends.
                      </p>
                    </div>
                  </div>
                )}


                {/* Image-based Questions Section */}
                {(!onlineTest.isImageBased || (onlineTest.isImageBased && testImageExpired)) && (
                  <>
{getImageQuestions().length > 0 && (
                  <div className="mb-8 sm:mb-12">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">
                      Image-Based Questions
                    </h2>
                    <div className="space-y-6 sm:space-y-8">
                      {getImageQuestions().map((question, index) => 
                        renderImageQuestion(question, index, {
                          button: 'bg-yellow-500 hover:bg-yellow-600',
                          timer: 'bg-red-500',
                          radio: 'text-yellow-500'
                        })
                      )}
                    </div>
                  </div>
                )}
                {/* Regular Text Questions Section */}
                {getTextQuestions().length > 0 && (
                  <div className="space-y-6 sm:space-y-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">
                      Text Questions
                    </h2>
                    {getTextQuestions().map((question, index) => (
                      <div
                        key={question._id}
                        className="bg-white border border-gray-300 rounded-lg p-4 sm:p-6 shadow-sm"
                      >
                        <h3 className="text-md sm:text-lg font-semibold text-gray-800 mb-4">
                          {`Question ${getImageQuestions().length + index + 1}: ${question.title}`}
                        </h3>
                        <div className="space-y-3">
                          {question.options.map((option, i) => (
                            <label
                              key={i}
                              className="flex items-center space-x-3 sm:space-x-4 cursor-pointer hover:bg-gray-100 p-3 rounded-lg transition duration-200"
                            >
                              <input
                                type="radio"
                                name={question._id}
                                value={option}
                                onChange={(e) => handleRegularQuestionAnswer(question._id, e.target.value)}
                                className="form-radio h-4 sm:h-5 w-4 sm:w-5 text-yellow-500"
                              />
                              <span className="text-gray-700 font-medium">
                                {option}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                </>
                )}
                

                

                <div className="mt-8 sm:mt-12 flex justify-end">
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="inline-flex items-center px-6 sm:px-8 py-2 sm:py-3 border border-transparent text-md sm:text-lg font-semibold rounded-full shadow-md text-white bg-yellow-500 disabled:opacity-50 transition-transform duration-300 ease-in-out transform hover:scale-110"
                  >
                    {submitting ? (
                      <>
                        <FiLoader className="animate-spin -ml-1 mr-3 h-4 sm:h-5 w-4 sm:w-5 text-white" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Assessment'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default Theme (any other businessId)
  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <img
            src={onlineTest.coverImage}
            alt="Test cover"
            className="w-full h-64 object-cover"
          />
          <div className="p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Final Assessment
            </h1>
            <div className="flex justify-between items-start mb-8">
              <div>
                <h2 className="text-xl font-semibold text-gray-700">
                  {onlineTest.clientId.name}
                </h2>
                <p className="text-gray-500">{onlineTest.clientId.email}</p>
              </div>
              <div className="text-right">
                <h3 className="text-lg font-semibold text-gray-700">
                  Instructions
                </h3>
                <p className="text-red-500 flex items-center justify-end mt-1">
                  <FiClock className="mr-1" />
                  {onlineTest.timeLimit} minutes to complete
                </p>
              </div>
            </div>

            {onlineTest.isReady ? (
              <>
                <div className="mb-8">
                  <MyTimer
                    expiryTimestamp={
                      new Date(
                        new Date(onlineTest.startedAt).getTime() +
                          onlineTest.timeLimit * 60000
                      )
                    }
                  />
                </div>
                

                {/* Monitoring Notice */}
                <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                  <div className="flex items-center">
                    <FiAlertCircle className="text-red-500 mr-3 h-5 w-5" />
                    <p className="text-red-800 font-medium">
                      You are monitored during this test
                    </p>
                  </div>
                </div>

                {(!onlineTest.isImageBased || (onlineTest.isImageBased && testImageExpired)) && (
                  <>
                  
                {/* Image-based Questions Section */}
                {getImageQuestions().length > 0 && (
                  <div className="mb-12">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                      Image-Based Questions
                    </h2>
                    <div className="space-y-6">
                      {getImageQuestions().map((question, index) => 
                        renderImageQuestion(question, index, {
                          button: 'bg-indigo-600 hover:bg-indigo-700',
                          timer: 'bg-red-500',
                          radio: 'text-indigo-600'
                        })
                      )}
                    </div>
                  </div>
                )}

                {/* Regular Text Questions Section */}
                {getTextQuestions().length > 0 && (
                  <div className="space-y-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                      Text Questions
                    </h2>
                    {getTextQuestions().map((question, index) => (
                      <div
                        key={question._id}
                        className="border-b border-gray-200 pb-6"
                      >
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                          {getImageQuestions().length + index + 1}. {question.title}
                        </h3>
                        <div className="space-y-4">
                          {question.options.map((option, i) => (
                            <label
                              key={i}
                              className="flex items-center space-x-3 cursor-pointer"
                            >
                              <input
                                type="radio"
                                name={question._id}
                                value={option}
                                onChange={(e) => handleRegularQuestionAnswer(question._id, e.target.value)}
                                className="form-radio h-5 w-5 text-indigo-600"
                              />
                              <span className="text-gray-700">{option}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                  </>
                )}

                      {/* Test Image Display for Image-based Tests */}
                {onlineTest.isImageBased && testImageVisible && !testImageExpired && (
                  <div className="mb-12 text-center">
                    <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-[#38afa9]">
                      <div className="mb-6">
                        <div className="inline-block px-6 py-3 bg-red-500 text-white rounded-full font-bold text-lg">
                          Image disappears in: {testImageTimer}s
                        </div>
                      </div>
                      <div className="mb-6">
                        <img
                          src={onlineTest.testImageUrl}
                          alt="Test Image"
                          className="max-w-full h-auto rounded-lg shadow-lg mx-auto max-h-96 object-contain"
                        />
                      </div>
                      <p className="text-gray-600 text-lg">
                        Study this image carefully. Questions will appear after the timer ends.
                      </p>
                    </div>
                  </div>
                )}




                <div className="mt-8 flex justify-end">
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <FiLoader className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Test'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleStartTest}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Start Test
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineTest;
